import { useState, useCallback } from "react";
import { isModuleInList, normalizeModuleId, deriveModuleSource, getIdsForName } from "../utils/moduleHelpers";
//TODO: Select from earliest module year + semester 
export const useModuleSelection = (initialRedo = [], initialProgression = [], initialCredit = []) => {
    const [selectedRedoSubjects, setSelectedRedoSubjects] = useState(initialRedo);
    const [selectedProgressionSubjects, setSelectedProgressionSubjects] = useState(initialProgression);
    const [creditModulesIds, setCreditModulesIds] = useState(initialCredit);
    const [allModulesState, setAllModulesState] = useState([]);

    const handleRedoModuleSelect = useCallback((moduleId) => {
        setSelectedRedoSubjects(prev => {
            if (isModuleInList(moduleId, prev)) {
                return prev.filter(id => normalizeModuleId(id) !== normalizeModuleId(moduleId));
            }
            return [...prev, moduleId];
        });
    }, []);

    const handleProgressionModuleSelect = useCallback((moduleId) => {
        setSelectedProgressionSubjects(prev => {
            if (isModuleInList(moduleId, prev)) {
                return prev.filter(id => normalizeModuleId(id) !== normalizeModuleId(moduleId));
            }
            return [...prev, moduleId];
        });
    }, []);

    const handleCreditModuleSelect = useCallback((moduleId) => {
        setCreditModulesIds(prev => {
            if (isModuleInList(moduleId, prev)) {
                return prev.filter(id => normalizeModuleId(id) !== normalizeModuleId(moduleId));
            }
            return [...prev, moduleId];
        });
    }, []);

    const handleAddModule = useCallback((module) => {
        const moduleId = module.netsuite_product_id;
        const source = deriveModuleSource(module);
        
        if (source === "redo") {
            if (isModuleInList(moduleId, selectedRedoSubjects)) {
                return;
            }
            setSelectedRedoSubjects(prev => [...prev, moduleId]);
        } else if (source === "progression") {
            if (isModuleInList(moduleId, selectedProgressionSubjects)) {
                return;
            }
            setSelectedProgressionSubjects(prev => [...prev, moduleId]);
        } else {
            if (isModuleInList(moduleId, creditModulesIds)) {
                return;
            }
            setCreditModulesIds(prev => [...prev, moduleId]);
        }
    }, [selectedRedoSubjects, selectedProgressionSubjects, creditModulesIds]);

    const handleRemoveModule = useCallback((module, type) => {
        if (!module) {
            return;
        }

        const primaryId = normalizeModuleId(module.netsuite_product_id);
        const allModulesArray = Array.isArray(allModulesState)
            ? allModulesState
            : Object.values(allModulesState || {});

        // Prefer matching by stable academic identifiers (module_id and code)
        const logicalIds = new Set();

        allModulesArray.forEach((m) => {
            const moduleIdMatches =
                module.module_id &&
                m.module_id &&
                String(m.module_id) === String(module.module_id);

            const codeMatches =
                (module.Code || module.code) &&
                (m.Code || m.code) &&
                String(m.Code || m.code) === String(module.Code || module.code);

            // Match either by shared module_id or by shared code
            if (moduleIdMatches || codeMatches) {
                logicalIds.add(normalizeModuleId(m.netsuite_product_id));
            }
        });

        // Fallback: also include any aliases that share the same name
        const aliasIds = getIdsForName(module.name, allModulesArray);
        aliasIds.forEach((id) => logicalIds.add(id));

        // Always make sure the primary id is included
        logicalIds.add(primaryId);

        const idsToRemove = logicalIds;
        setSelectedRedoSubjects(prev => prev.filter(id => !idsToRemove.has(normalizeModuleId(id))));
        setSelectedProgressionSubjects(prev => prev.filter(id => !idsToRemove.has(normalizeModuleId(id))));
        setCreditModulesIds(prev => prev.filter(id => !idsToRemove.has(normalizeModuleId(id))));
    }, [allModulesState]);

    return {
        selectedRedoSubjects,
        selectedProgressionSubjects,
        creditModulesIds,
        setSelectedRedoSubjects,
        setSelectedProgressionSubjects,
        setCreditModulesIds,
        setAllModulesState,
        handleRedoModuleSelect,
        handleProgressionModuleSelect,
        handleCreditModuleSelect,
        handleAddModule,
        handleRemoveModule
    };
};
